import { useEffect, useRef } from 'react';
import type { MutableRefObject } from 'react';
import { useSelector } from 'react-redux';
import { updateTheme } from '../../state/features/theme/operations';
import type { RootState } from '../../state/store';
import styled from 'styled-components';
import { IoIosMoon, IoIosSunny } from 'react-icons/io';

const ThemeToggleSwitch = () => {
  const { theme } = useSelector((state: RootState) => state);
  const inputRef = useRef() as MutableRefObject<HTMLInputElement>;

  useEffect(() => {
    if (theme.mode === 'dark') {
      inputRef.current.checked = true;
    }
  }, [theme.mode]);

  return (
    <ToggleSwitch>
      <label className="switch">
        <input
          ref={inputRef}
          type="checkbox"
          onChange={(e) => updateTheme(e.target.checked ? 'dark' : 'light')}
        />
        <span className="slider round">
          <IoIosSunny fontSize="19px" className="theme-icon sun" />
          <IoIosMoon fontSize="19px" className="theme-icon moon" />
        </span>
      </label>
    </ToggleSwitch>
  );
};

const ToggleSwitch = styled.div`
  align-content: center;
  .switch {
    position: relative;
    display: inline-block;
    width: 60px;
    height: 34px;
    .slider {
      position: absolute;
      cursor: pointer;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: ${({ theme }) => theme.body};
      -webkit-transition: 0.4s;
      transition: 0.4s;
      &::before {
        position: absolute;
        content: '';
        height: 26px;
        width: 26px;
        left: 4px;
        bottom: 3px;
        background-color: ${({ theme }) => theme.body};
        -webkit-transition: 0.4s;
        transition: 0.4s;
        z-index: 1;
        box-shadow: 0 0 4px rgba(0, 0, 0, 0.15);
      }
      > .theme-icon {
        position: absolute;
        z-index: 10;
        top: 7px;
        transition: all 400ms cubic-bezier(0.115, 1.195, 0.245, 1.195);
        &.sun {
          left: 7px;
        }
        &.moon {
          right: 5px;
        }
      }
    }
    input {
      opacity: 0;
      width: 0;
      height: 0;
      & + span {
        > .moon {
          color: ${({ theme }) => theme.text};
          transform: scale(0.8);
        }
        > .sun {
          color: ${({ theme }) => theme.text};
          transform: scale(1);
        }
      }
      &:checked + span {
        > .sun {
          color: ${({ theme }) => theme.text};
          transform: scale(0.8);
        }
        > .moon {
          color: ${({ theme }) => theme.text};
          transform: scale(1);
        }
      }
    }
  }
  input:checked + .slider {
    background-color: ${({ theme }) => theme.body};
  }

  input:focus + .slider {
    box-shadow: 0 0 1px #2196f3;
  }
  input:checked + .slider:before {
    -webkit-transform: translateX(26px);
    -ms-transform: translateX(26px);
    transform: translateX(26px);
  }
  .slider {
    &.round {
      border-radius: 34px;
      border: 1px solid ${({ theme }) => theme.background};
      transition: all 400ms cubic-bezier(0.115, 1.195, 0.245, 1.195);
    }
    &.round::before {
      border-radius: 50%;
      background: ${({ theme }) => theme.background};
    }
  }
`;

export default ThemeToggleSwitch;
